import type { FC, ReactNode } from 'react'
import type { SelectGroup } from '@/components/forms/fields'

import { useRequestForm, useTodayDate } from '@/components/forms/use-request-form'
import { FormSuccess } from '@/components/forms/form-success'
import { CurrencyPicker } from '@/components/forms/currency-picker'
import { diasporaCountries } from '@/constants/site'

import {
    Field,
    FormAlert,
    Honeypot,
    SelectInput,
    SubmitButton,
    TextArea,
    TextInput
} from '@/components/forms/fields'

interface DiasporaFormProps {
    itemGroups: Array<SelectGroup>
    initialItem?: string
    initialCurrency?: string
}

export const CUSTOM_ORDER = 'A custom order (I will describe it below)'

const legendStyles = 'mb-5 flex items-center gap-3 font-heading text-2xl font-semibold text-brand-charcoal'
const stepStyles = 'inline-flex size-8 items-center justify-center rounded-full bg-brand-charcoal font-sans text-xs font-semibold text-brand-white'

export const DiasporaForm: FC<DiasporaFormProps> = ({
    itemGroups,
    initialItem = '',
    initialCurrency = ''
}): ReactNode => {
    const formId = 'diaspora'
    const today = useTodayDate()

    const {
        values,
        errors,
        status,
        reference,
        message,
        setField,
        handleSubmit,
        reset,
        fieldProps
    } = useRequestForm({
        type: 'diaspora',
        formId,
        initialValues: { item: initialItem, currency: initialCurrency }
    })

    if (status === 'success') {
        return (
            <FormSuccess
                title='Your order is on its way to our kitchen.'
                description={(
                    <p>
                        Thank you for thinking of your loved ones. We will contact you on WhatsApp or by email to confirm the menu, the delivery details and the total in your preferred currency, together with the payment options. Nothing is charged until you confirm.
                    </p>
                )}
                reference={reference}
                whatsappMessage="Hello Loveth Tasty, I just placed an order from abroad on your website."
                onReset={reset}
                resetLabel='Place another order'
            />
        )
    }

    return (
        <form id={formId} onSubmit={handleSubmit} noValidate className='relative space-y-10'>
            <Honeypot />

            <fieldset>
                <legend className={legendStyles}>
                    <span className={stepStyles} aria-hidden='true'>1</span>
                    About you
                </legend>

                <div className='grid gap-5 sm:grid-cols-2'>
                    <Field id={`${formId}-name`} label='Your full name' required error={errors.name}>
                        <TextInput {...fieldProps('name')} autoComplete='name' aria-required='true' placeholder='Your full name' />
                    </Field>

                    <Field id={`${formId}-country`} label='Country you live in' required error={errors.country}>
                        <SelectInput {...fieldProps('country')} autoComplete='country-name' aria-required='true' placeholder='Choose your country' options={diasporaCountries} />
                    </Field>

                    <Field id={`${formId}-email`} label='Email address' required error={errors.email}>
                        <TextInput {...fieldProps('email')} type='email' autoComplete='email' inputMode='email' aria-required='true' placeholder='you@example.com' />
                    </Field>

                    <Field id={`${formId}-phone`} label='Your WhatsApp number' required error={errors.phone} hint='Include your country code, for example +44 7700 900123.'>
                        <TextInput {...fieldProps('phone')} type='tel' autoComplete='tel' inputMode='tel' aria-required='true' placeholder='+44 7700 900123' />
                    </Field>
                </div>
            </fieldset>

            <fieldset>
                <legend className={legendStyles}>
                    <span className={stepStyles} aria-hidden='true'>2</span>
                    Your order
                </legend>

                <div className='grid gap-5 sm:grid-cols-2'>
                    <Field id={`${formId}-item`} label='What would you like to send?' required error={errors.item}>
                        <SelectInput
                            {...fieldProps('item')}
                            aria-required='true'
                            placeholder='Choose a meal, tray or package'
                            groups={itemGroups}
                            options={[CUSTOM_ORDER]}
                        />
                    </Field>

                    <Field id={`${formId}-requested_date`} label='Delivery date' required error={errors.requested_date}>
                        <TextInput {...fieldProps('requested_date')} type='date' min={today || undefined} aria-required='true' />
                    </Field>
                </div>

                <fieldset className='mt-6'>
                    <legend className='mb-3 text-[13px] font-semibold text-brand-charcoal'>
                        Preferred payment currency
                        <span aria-hidden='true' className='text-brand-ember'> *</span>
                    </legend>

                    <CurrencyPicker
                        name='currency'
                        idPrefix={`${formId}-currency`}
                        value={values.currency}
                        onChange={currency => setField('currency', currency)}
                        invalid={Boolean(errors.currency)}
                        describedBy={errors.currency ? `${formId}-currency-error` : undefined}
                    />

                    {errors.currency
                        ? <p id={`${formId}-currency-error`} className='mt-2 text-[13px] font-medium text-destructive'>{errors.currency}</p>
                        : <p className='mt-2 text-xs text-brand-stone'>We confirm your total in this currency and share the payment options with you.</p>}
                </fieldset>
            </fieldset>

            <fieldset>
                <legend className={legendStyles}>
                    <span className={stepStyles} aria-hidden='true'>3</span>
                    Delivery in Nigeria
                </legend>

                <div className='grid gap-5 sm:grid-cols-2'>
                    <Field id={`${formId}-recipient_name`} label='Recipient name' required error={errors.recipient_name}>
                        <TextInput {...fieldProps('recipient_name')} aria-required='true' placeholder='Who is receiving the order?' />
                    </Field>

                    <Field id={`${formId}-recipient_phone`} label='Recipient phone number' required error={errors.recipient_phone}>
                        <TextInput {...fieldProps('recipient_phone')} type='tel' inputMode='tel' aria-required='true' placeholder='0803 000 0000' />
                    </Field>

                    <Field id={`${formId}-location`} label='Delivery address' required error={errors.location} hint='House number, street, area and city.' className='sm:col-span-2'>
                        <TextArea {...fieldProps('location')} rows={3} aria-required='true' placeholder='For example, 12 Oba Adesida Road, Akure' className='min-h-24' />
                    </Field>

                    <Field id={`${formId}-message`} label='A note for your loved one or special instructions' error={errors.message} className='sm:col-span-2'>
                        <TextArea {...fieldProps('message')} placeholder='Happy birthday Mum, we love you. Please call before delivery.' />
                    </Field>
                </div>
            </fieldset>

            <FormAlert message={message} />

            <div className='flex flex-col-reverse gap-4 border-t border-brand-sand pt-6 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-sm text-xs leading-relaxed text-brand-stone'>
                    Sending this form does not charge you. We confirm the order, delivery and payment with you first.
                </p>

                <SubmitButton submitting={status === 'submitting'} label='Send My Order' />
            </div>
        </form>
    )
}
